import { clsx } from 'clsx'
import { LuCheck, LuX } from 'react-icons/lu'

import { AlertSeverityBadge } from '@/feature/alerts/components/AlertSeverityBadge'
import {
  ALERT_SEVERITY_BY_TEMPLATE,
  AlertTemplateKey,
} from '@/feature/alerts/constants/alertContract'
import type { AlertTemplateConfig } from '@/feature/alerts/constants/alertTemplates'
import {
  TaskAssignmentCreated,
  TaskAssignmentRejected,
  TaskAssignmentStillness,
  TaskEndVehicleArrived,
  TaskEndVehicleLate,
  TaskEndVehiclePossiblyLate,
  TaskEndVehicleStillLate,
  TaskStartVehicleArrived,
  TaskStartVehicleLate,
  TaskStartVehicleNotAssigned,
  TaskStartVehiclePossiblyLate,
  TaskStartVehicleStillLate,
  TaskStateScheduled,
} from '@/feature/alerts/constants/alertTemplates'
import { useAlertStore } from '@/feature/alerts/store'
import { ModalBase } from '@/lib/core/components/Modal/ModalBase'

import styles from './CreateAlertModal.module.css'

interface AlertTemplateItemProps {
  config: AlertTemplateConfig
}

const AlertTemplateItem = ({ config }: AlertTemplateItemProps) => {
  const { selectedTemplate, setSelectedTemplate } = useAlertStore()
  const isSelected = selectedTemplate?.key === config.key
  const severity = ALERT_SEVERITY_BY_TEMPLATE[config.key as AlertTemplateKey]

  return (
    <li
      className={clsx(styles.item, isSelected && styles.itemSelected)}
      onClick={() => setSelectedTemplate(isSelected ? null : config)}
    >
      <span className={styles.itemName}>{config.title}</span>
      <div className={styles.itemRight}>
        {severity && <AlertSeverityBadge severity={severity} />}
        {isSelected && (
          <span className={styles.checkIcon}>
            <LuCheck size={16} />
          </span>
        )}
      </div>
    </li>
  )
}

const TemplateList = () => (
  <div className={styles.groups}>
    <div className={styles.group}>
      <h3 className={styles.groupHeading}>Task Assignment Alerts</h3>
      <ul className={styles.list}>
        <AlertTemplateItem config={TaskAssignmentCreated} />
        <AlertTemplateItem config={TaskAssignmentStillness} />
        <AlertTemplateItem config={TaskAssignmentRejected} />
        <AlertTemplateItem config={TaskStateScheduled} />
        <AlertTemplateItem config={TaskStartVehicleNotAssigned} />
      </ul>
    </div>

    <div className={styles.group}>
      <h3 className={styles.groupHeading}>Start Location Alerts</h3>
      <ul className={styles.list}>
        <AlertTemplateItem config={TaskStartVehiclePossiblyLate} />
        <AlertTemplateItem config={TaskStartVehicleLate} />
        <AlertTemplateItem config={TaskStartVehicleStillLate} />
        <AlertTemplateItem config={TaskStartVehicleArrived} />
      </ul>
    </div>

    <div className={styles.group}>
      <h3 className={styles.groupHeading}>End Location Alerts</h3>
      <ul className={styles.list}>
        <AlertTemplateItem config={TaskEndVehiclePossiblyLate} />
        <AlertTemplateItem config={TaskEndVehicleLate} />
        <AlertTemplateItem config={TaskEndVehicleStillLate} />
        <AlertTemplateItem config={TaskEndVehicleArrived} />
      </ul>
    </div>
  </div>
)

export const CreateAlertModal = () => {
  const { isCreateAlertOpen, closeCreateAlert, openCoreAlert, selectedTemplate } = useAlertStore()

  const handleNext = () => {
    openCoreAlert()
  }

  return (
    <ModalBase isActive={isCreateAlertOpen} onEscape={closeCreateAlert}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Create New Alert</span>
          <button className={styles.closeButton} type="button" onClick={closeCreateAlert}>
            <LuX size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <TemplateList />
        </div>

        <div className={styles.footer}>
          <button
            className={clsx(styles.footerButton, styles.cancelButton)}
            type="button"
            onClick={closeCreateAlert}
          >
            Cancel
          </button>
          <button
            className={clsx(styles.footerButton, styles.nextButton)}
            type="button"
            onClick={handleNext}
            disabled={!selectedTemplate}
          >
            Next
          </button>
        </div>
      </div>
    </ModalBase>
  )
}
